/* Plain geometry on millimetre rectangles. */

import type { Item } from './model.js';

export interface Rect { x: number; y: number; w: number; h: number }

const EPS = 1e-6;

/** Axis-aligned bounding box of an item (any rotation, centre-based). */
export function itemBox(it: Pick<Item, 'cx' | 'cy' | 'widthMm' | 'heightMm' | 'rotation'>): Rect {
  const r = (((it.rotation % 360) + 360) % 360) * Math.PI / 180;
  const c = Math.abs(Math.cos(r)), s = Math.abs(Math.sin(r));
  // snap tiny float noise at right angles
  const cc = c < 1e-9 ? 0 : c > 1 - 1e-9 ? 1 : c;
  const ss = s < 1e-9 ? 0 : s > 1 - 1e-9 ? 1 : s;
  const w = it.widthMm * cc + it.heightMm * ss;
  const h = it.widthMm * ss + it.heightMm * cc;
  return { x: it.cx - w / 2, y: it.cy - h / 2, w, h };
}

/** True when the rotation is a multiple of 90° (box == artwork footprint). */
export function isRightAngle(rotation: number): boolean {
  const r = ((rotation % 90) + 90) % 90;
  return r < 1e-6 || 90 - r < 1e-6;
}

export function quarterTurns(rotation: number): 0 | 1 | 2 | 3 {
  return ((Math.round(rotation / 90) % 4) + 4) % 4 as 0 | 1 | 2 | 3;
}

export function intersects(a: Rect, b: Rect, tol = EPS): boolean {
  return a.x < b.x + b.w - tol && b.x < a.x + a.w - tol && a.y < b.y + b.h - tol && b.y < a.y + a.h - tol;
}

/** Gap between two rectangles (0 when they touch or overlap). */
export function rectGap(a: Rect, b: Rect): number {
  const dx = Math.max(0, Math.max(a.x, b.x) - Math.min(a.x + a.w, b.x + b.w));
  const dy = Math.max(0, Math.max(a.y, b.y) - Math.min(a.y + a.h, b.y + b.h));
  if (dx === 0) return dy;
  if (dy === 0) return dx;
  return Math.hypot(dx, dy);
}

export function inside(inner: Rect, outer: Rect, tol = 1e-3): boolean {
  return inner.x >= outer.x - tol && inner.y >= outer.y - tol &&
    inner.x + inner.w <= outer.x + outer.w + tol && inner.y + inner.h <= outer.y + outer.h + tol;
}

export function union(rs: Rect[]): Rect | null {
  if (!rs.length) return null;
  let x0 = Infinity, y0 = Infinity, x1 = -Infinity, y1 = -Infinity;
  for (const r of rs) {
    x0 = Math.min(x0, r.x); y0 = Math.min(y0, r.y);
    x1 = Math.max(x1, r.x + r.w); y1 = Math.max(y1, r.y + r.h);
  }
  return { x: x0, y: y0, w: x1 - x0, h: y1 - y0 };
}

/** Bottom-most edge of the content on a sheet (mm), 0 if empty. */
export function contentBottom(items: Item[]): number {
  let b = 0;
  for (const it of items) { const r = itemBox(it); b = Math.max(b, r.y + r.h); }
  return b;
}

/** Pairs of items that overlap or sit closer than `gap` (mm). Grid-bucketed: fine for thousands of items. */
export function closePairs(items: Item[], gap: number, tol = 0.05): Array<{ a: number; b: number; overlap: boolean; distance: number }> {
  const boxes = items.map(itemBox);
  const size = Math.max(60, gap * 2 + 1);
  const grid = new Map<string, number[]>();
  const keyOf = (cx: number, cy: number) => cx + ',' + cy;
  boxes.forEach((r, i) => {
    const x0 = Math.floor((r.x - gap) / size), x1 = Math.floor((r.x + r.w + gap) / size);
    const y0 = Math.floor((r.y - gap) / size), y1 = Math.floor((r.y + r.h + gap) / size);
    for (let gx = x0; gx <= x1; gx++) for (let gy = y0; gy <= y1; gy++) {
      const k = keyOf(gx, gy);
      const list = grid.get(k);
      if (list) list.push(i); else grid.set(k, [i]);
    }
  });
  const seen = new Set<string>();
  const out: Array<{ a: number; b: number; overlap: boolean; distance: number }> = [];
  for (const list of grid.values()) {
    for (let p = 0; p < list.length; p++) for (let q = p + 1; q < list.length; q++) {
      const a = Math.min(list[p], list[q]), b = Math.max(list[p], list[q]);
      const k = a + ':' + b;
      if (seen.has(k)) continue;
      seen.add(k);
      const ra = boxes[a], rb = boxes[b];
      const ov = intersects(ra, rb, tol);
      const d = ov ? 0 : rectGap(ra, rb);
      if (ov || d < gap - tol) out.push({ a, b, overlap: ov, distance: d });
    }
  }
  return out;
}
